import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/components/utils/utils";
import { useI18n } from "@/hooks/useI18n";

export interface TravelDesignerFilterProps {
  selectedDesigner: string | null;
  onDesignerChange: (designer: string | null) => void;
  itineraries: { assignedAdvisor?: { email: string; firstName: string; lastName: string; fullName: string } | null }[];
  onFilteredItinerariesChange: (filteredItineraries: any[]) => void;
  disabled?: boolean;
}

export const TravelDesignerFilter: React.FC<TravelDesignerFilterProps> = ({
  selectedDesigner,
  onDesignerChange,
  itineraries,
  onFilteredItinerariesChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useI18n();

  // Extract unique travel designers directly from itineraries
  const designers = useMemo(() => {
    const designerMap = new Map<string, { email: string; firstName: string; lastName: string; name: string }>();

    // Collect all unique designers by email from itineraries
    itineraries.forEach((itinerary) => {
      const advisor = itinerary?.assignedAdvisor;
      if (advisor?.email) {
        designerMap.set(advisor.email, {
          email: advisor.email,
          firstName: advisor.firstName || '',
          lastName: advisor.lastName || '',
          name: advisor.fullName || `${advisor.firstName || ''} ${advisor.lastName || ''}`.trim()
        });
      }
    });

    // Convert map to array and sort by name
    const result = Array.from(designerMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [itineraries]);

  // Filter designers based on search term (search in name, email, first name, last name)
  const filteredDesigners = designers.filter(designer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      designer.name.toLowerCase().includes(searchLower) ||
      designer.email.toLowerCase().includes(searchLower) ||
      designer.firstName.toLowerCase().includes(searchLower) ||
      designer.lastName.toLowerCase().includes(searchLower)
    );
  });

  // Apply filter to itineraries when selectedDesigner changes
  useEffect(() => {
    if (!onFilteredItinerariesChange) return;

    if (!selectedDesigner) {
      // No filter selected, show all itineraries
      onFilteredItinerariesChange(itineraries);
      return;
    }

    // Find the designer email from the selected name
    const designer = designers.find(d => d.name === selectedDesigner);
    if (!designer) {
      onFilteredItinerariesChange(itineraries);
      return;
    }

    // Filter itineraries by designer email using assignedAdvisor.email
    const filtered = itineraries.filter(itinerary => {
      return itinerary.assignedAdvisor?.email === designer.email;
    });

    onFilteredItinerariesChange(filtered);
  }, [selectedDesigner, itineraries, designers, onFilteredItinerariesChange]);

  const handleDesignerSelect = (designerName: string) => {
    if (selectedDesigner === designerName) {
      onDesignerChange(null);
    } else {
      onDesignerChange(designerName);
    }
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClearFilter = () => {
    onDesignerChange(null);
    setSearchTerm("");
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="flex items-center gap-2 min-w-[140px] justify-between"
        >
          <span className="truncate">
            {selectedDesigner ? selectedDesigner : t('role.travelAdvisor')}
          </span>
          <div className="flex items-center gap-1">
            <ChevronDown className="h-4 w-4" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="p-3">
          <Input
            placeholder={t('travelRequest.searchTravelAdvisor')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-2"
          />
          <div className="max-h-48 overflow-y-auto">
            {filteredDesigners.length > 0 ? (
              filteredDesigners.map((designer) => (
                <button
                  key={designer.email}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center justify-between",
                    selectedDesigner === designer.name && "bg-blue-50"
                  )}
                  onClick={() => handleDesignerSelect(designer.name)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{designer.name}</span>
                    <span className="text-xs text-gray-500">{designer.email}</span>
                  </div>
                  {selectedDesigner === designer.name && (
                    <Check className="h-4 w-4 text-blue-600" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {t('travelRequest.noTravelAdvisorsFound')}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
